import React from 'react';
import { GroupPostAttributesType, MediaTypePostEditorState } from '../../../../models/post-editor';
import { BoxNotification } from '../../../../components/containers/BoxNotification';
import { PostCardPreviewMediaDataType } from '../PostCardMediaPreview/PostCardMediaPreview.view';
import { PostCardMediaPreview } from '../PostCardMediaPreview';
import {
  LinkGoogleCallToAction,
  MediaType,
  PostMultiMediaAttachMode,
  PostPublishMethod
} from '../../../../models/graphql';
import { getGreatestCommonDivisor } from '../../../../helpers/calculateVariations';
import { initialParagraph } from '../../states/initial';
import { SelectedSocialProfileValue } from '../../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { SocialProfileWithBoardsAndMentionType } from '../../helpers/mapSelectedProfilesValues';

export type PostCardMediaPreviewWrapperViewProps = {
  //
  post: GroupPostAttributesType;
  profiles: SocialProfileWithBoardsAndMentionType[];
  stateProfiles: SelectedSocialProfileValue[];
  context: 'queue' | 'postEditor';
};

const getCurrentIndex = (i: number, length: number): number => {
  const ceil = Math.floor(i / length);
  return i - ceil * length;
};

const getVariations = (post: GroupPostAttributesType): PostCardPreviewMediaDataType[] => {
  const { firstComment, shouldShowFirstComment, media, texts, link, varyMedia } = post.content;
  const total =
    media.length > 1 && varyMedia === PostMultiMediaAttachMode.ALTERNATE
      ? texts.length * (media.length / getGreatestCommonDivisor(texts.length, media.length))
      : texts.length;
  const aArr = Array(texts.length)
    .fill(1)
    .map((_, i) => i); // 0,1,2,3
  const bArr = Array(media.length)
    .fill(1)
    .map((_, i) => i); // 0,1,2
  if (varyMedia === PostMultiMediaAttachMode.ALTERNATE) {
    return Array(total)
      .fill(1)
      .map((_, i) => {
        const aIndex = i + 1 > aArr.length ? aArr[getCurrentIndex(i, aArr.length)] : aArr[i] ?? null;
        const bIndex = i + 1 > bArr.length ? bArr[getCurrentIndex(i, bArr.length)] : bArr[i] ?? null;
        return {
          text: texts.length ? texts[aIndex].text : [initialParagraph()],
          media: media.length ? [media[bIndex]] : [],
          firstComment: shouldShowFirstComment ? firstComment : null,
          link: link
        };
      });
  } else if (varyMedia === PostMultiMediaAttachMode.MULTIPLE) {
    return Array(total)
      .fill(1)
      .map((_, i) => {
        const aIndex = i + 1 > aArr.length ? aArr[getCurrentIndex(i, aArr.length)] : aArr[i] ?? null;
        return {
          text: texts[aIndex].text,
          media: media,
          firstComment: shouldShowFirstComment ? firstComment : null,
          link: link
        };
      });
  }
  return [];
};

const isVideo = (media: MediaTypePostEditorState[]) => {
  if (media.length !== 1) return false;
  return media[0].type === MediaType.VIDEO;
};

export const PostCardMediaPreviewWrapperView = ({
  post,
  profiles,
  stateProfiles,
  context
}: PostCardMediaPreviewWrapperViewProps): JSX.Element => {
  const selectedProfile = post.customizedProfileId ? profiles.find(p => p.id === post.customizedProfileId) : null;
  const selectedStateProfile = stateProfiles.find(p => p.profileId === post.customizedProfileId);
  if (!selectedProfile) return <BoxNotification type={'error'}>We could not map the post preview.</BoxNotification>;
  const data: PostCardPreviewMediaDataType[] = getVariations(post);
  const hasTitle = isVideo(post.content.media) && post.content.title;
  let currentVariationIndex: number;
  switch (context) {
    case 'queue': {
      currentVariationIndex = 0;
      break;
    }
    case 'postEditor':
    default: {
      currentVariationIndex = post.content.variationMeta.currentVariationIndex - 1;
      break;
    }
  }

  // const profileTypeName =
  //   selectedProfile.__typename === 'GenericSocialProfile' ? 'GenericSocialProfile' : selectedProfile.__typename;

  switch (selectedProfile.__typename) {
    case 'GenericSocialProfile':
      // NOTE: this is for initial implemenation for UP's Phase1. We will need to refactor the way the logic flows to
      // address how UPs have use of all existing profile types (per se) as well as many or unlimited others.
      return selectedProfile.genericProfile === 'FACEBOOK_PERSONAL' ||
        selectedProfile.genericProfile === 'FACEBOOK_GROUP' ? (
        <PostCardMediaPreview
          // @ts-ignore
          socialProfile={{ ...selectedProfile, __typename: 'FacebookSocialProfile' }}
          data={data}
          currentVariationIndex={currentVariationIndex}
          publishMethod={PostPublishMethod.DIRECT}
        />
      ) : selectedProfile.genericProfile === 'INSTAGRAM_PERSONAL' ? (
        <PostCardMediaPreview
          // @ts-ignore
          socialProfile={{ ...selectedProfile, __typename: 'InstagramSocialProfile' }}
          data={data}
          igType={post.content.postFeedType}
          publishMethod={PostPublishMethod.DIRECT}
          currentVariationIndex={currentVariationIndex}
        />
      ) : (
        <></>
      );
    case 'FacebookSocialProfile':
      return (
        <PostCardMediaPreview
          socialProfile={{ ...selectedProfile, __typename: 'FacebookSocialProfile' }}
          data={data}
          currentVariationIndex={currentVariationIndex}
          // We need to reduce variation index since post state data start from 1 not from index 0
        />
      );
    case 'TwitterSocialProfile':
      return (
        <PostCardMediaPreview
          socialProfile={{ ...selectedProfile, __typename: 'TwitterSocialProfile' }}
          data={data}
          currentVariationIndex={currentVariationIndex}
        />
      );
    case 'LinkedInSocialProfile':
      return (
        <PostCardMediaPreview
          socialProfile={{ ...selectedProfile, __typename: 'LinkedInSocialProfile' }}
          data={data}
          currentVariationIndex={currentVariationIndex}
          {...(hasTitle ? { title: post.content.title || undefined } : {})}
        />
      );
    case 'GoogleSocialProfile':
      return (
        <PostCardMediaPreview
          socialProfile={{ ...selectedProfile, __typename: 'GoogleSocialProfile' }}
          data={data}
          currentVariationIndex={currentVariationIndex}
          buttonType={
            post.content.link
              ? post.content.link.googleCallToAction || LinkGoogleCallToAction.NONE
              : LinkGoogleCallToAction.NONE
          }
          {...(hasTitle ? { title: post.content.title || undefined } : {})}
        />
      );
    case 'PinterestSocialProfile':
      return (
        <PostCardMediaPreview
          socialProfile={{ ...selectedProfile, __typename: 'PinterestSocialProfile' }}
          selectedBoardId={selectedStateProfile?.pnBoardId || undefined}
          data={data}
          currentVariationIndex={currentVariationIndex}
          {...(post.content.title ? { title: post.content.title } : {})}
        />
      );
    case 'InstagramSocialProfile':
      return (
        <PostCardMediaPreview
          socialProfile={{ ...selectedProfile, __typename: 'InstagramSocialProfile' }}
          data={data}
          igType={post.content.postFeedType}
          publishMethod={post.content.publishMethod}
          currentVariationIndex={currentVariationIndex}
        />
      );
    case 'TikTokSocialProfile':
      return (
        <PostCardMediaPreview
          socialProfile={{ ...selectedProfile, __typename: 'TikTokSocialProfile' }}
          data={data}
          privacy={selectedStateProfile?.tikTok?.privacyTarget || null}
          allowedActions={selectedStateProfile?.tikTok?.allowedActions || []}
          currentVariationIndex={currentVariationIndex}
        />
      );
    default:
      return <></>;
  }
};
